

import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../supabase';
import { BookmarkIcon } from '../icons/InterfaceIcons';
import { parseTextWithImages } from '../parseTextWithImages';

// Fix: Define an interface for bookmark objects to resolve type errors.
interface Bookmark {
    created_at: string;
    question_id: string;
}

// This is almost identical to the card in ExamView, so let's create it here.
// FIX: Add explicit types for props to allow 'key' prop in map.
const BookmarkedQuestionCard: React.FC<{ question: any; onToggleBookmark: any; }> = ({ question, onToggleBookmark }) => {
    const [showAnswer, setShowAnswer] = useState(false);

    const getDifficultyColor = (difficulty) => {
        if (difficulty === 'Easy') return 'bg-green-100 text-green-800';
        if (difficulty === 'Medium') return 'bg-yellow-100 text-yellow-800';
        if (difficulty === 'Hard') return 'bg-red-100 text-red-800';
        return 'bg-gray-100 text-gray-800';
    }

    return (
        <div className="bg-brand-surface border border-brand-border rounded-xl p-6 relative">
            <button
                onClick={() => onToggleBookmark(question.id)}
                className="absolute top-4 right-4 p-2 rounded-full text-brand-primary bg-brand-primary/10 hover:bg-brand-primary/20"
                aria-label="Remove bookmark"
            >
                <BookmarkIcon filled={true} className="w-5 h-5" />
            </button>
            <div className="flex flex-wrap gap-2 mb-4 text-xs font-semibold">
                <span className="px-2 py-1 rounded-full bg-brand-primary/10 text-brand-primary">{question.subject}</span>
                <span className="px-2 py-1 rounded-full bg-brand-secondary/10 text-brand-secondary">{question.topic}</span>
                <span className={`px-2 py-1 rounded-full ${getDifficultyColor(question.difficulty)}`}>{question.difficulty}</span>
            </div>
            <div className="text-brand-text-primary mb-4 pr-10">{parseTextWithImages(question.text)}</div>
            <ul className="space-y-2 mb-4">
                {question.options.map((option, index) => (
                    <li key={index} className="flex items-start text-sm">
                        <span className="font-semibold mr-2">{String.fromCharCode(65 + index)}.</span>
                        <div className="flex-1">{parseTextWithImages(option)}</div>
                    </li>
                ))}
            </ul>
            <button
                onClick={() => setShowAnswer(!showAnswer)}
                className="text-sm font-semibold text-brand-primary hover:underline"
            >
                {showAnswer ? 'Hide Answer' : 'Show Answer'}
            </button>
            {showAnswer && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg animate-fade-in">
                    <div className="text-sm font-bold text-green-800">Correct Answer: {parseTextWithImages(question.answer)}</div>
                    {question.solution && <div className="text-sm text-green-700 mt-2">{parseTextWithImages(question.solution)}</div>}
                </div>
            )}
        </div>
    );
};

const getDifficultyFromLevel = (level) => {
    switch (level) {
        case 1: return 'Easy';
        case 2: return 'Medium';
        case 3: return 'Hard';
        default: return 'Medium';
    }
};

export const BookmarksView = ({ user, onToggleBookmark }) => {
    const [allQuestions, setAllQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const bookmarkedQuestionIds = useMemo(() => {
        const questions = user.bookmarks?.questions;
        if (!questions) return [];
        // Fix: Cast the array of bookmarks to the correct type to allow sorting.
        return (Object.values(questions) as Bookmark[])
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .map(b => b.question_id);
    }, [user.bookmarks]);


    useEffect(() => {
        const fetchAllQuestions = async () => {
            setIsLoading(true);
            try {
                const examIds = ['jee_main', 'jee_advanced', 'neet', 'bitsat_questions', 'nda', 'vitee'];
                const questionPromises = examIds.map(async (examId) => {
                    const { data, error } = await supabase.from(examId).select('*');
                    if (error) {
                        console.warn(`Could not fetch questions for ${examId}:`, error.message);
                        return [];
                    }
                    return data ? data.map((q) => ({
                        id: q.id.toString(),
                        text: q.question,
                        options: [q.option1, q.option2, q.option3, q.option4].filter(opt => opt),
                        answer: examId === 'vitee' ? q.correct_option : q.correct,
                        solution: examId === 'vitee' ? q.solution : q.explanation,
                        subject: q.subject || 'General',
                        topic: (examId === 'vitee' ? q.chapter : q.tags) || 'Unknown Topic',
                        difficulty: getDifficultyFromLevel(q.level),
                        type: q.type || 'Single Choice',
                    })) : [];
                });
    
                const questionArrays = await Promise.all(questionPromises);
                const allFetchedQuestions = questionArrays.flat();
                
                setAllQuestions(allFetchedQuestions);

            } catch (error) {
                console.error("Error fetching all questions for bookmarks:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllQuestions();
    }, []); // Run only once on mount

    const bookmarkedQuestions = useMemo(() => {
        if (bookmarkedQuestionIds.length === 0 || allQuestions.length === 0) return [];
        const questionsMap = new Map(allQuestions.map(q => [q.id, q]));
        return bookmarkedQuestionIds.map(id => questionsMap.get(id)).filter(q => !!q);
    }, [bookmarkedQuestionIds, allQuestions]);


    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-10">
                <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
                <p className="ml-4 text-brand-text-secondary">Loading your bookmarks...</p>
            </div>
        );
    }
    
    if (bookmarkedQuestions.length === 0) {
         return (
            <div className="text-center py-16 bg-brand-surface rounded-lg border border-brand-border animate-fade-in">
                <BookmarkIcon className="mx-auto h-12 w-12 text-brand-text-secondary" />
                <h3 className="mt-4 text-lg font-semibold text-brand-text-primary">No Bookmarked Questions</h3>
                <p className="text-brand-text-secondary mt-1">You can bookmark questions while practicing to review them here later.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <h2 className="text-2xl font-bold text-brand-text-primary">My Bookmarked Questions ({bookmarkedQuestions.length})</h2>
            {bookmarkedQuestions.map(question => (
                <BookmarkedQuestionCard 
                    key={question.id}
                    question={question}
                    onToggleBookmark={onToggleBookmark}
                />
            ))}
        </div>
    );
};